/**
 * Impersonation — an Admin seeing and using the app exactly as one Staff member
 * does. The rules; the mechanics are in src/adapters/auth/impersonation.ts and
 * the whole picture is in docs/IMPERSONATION.md.
 *
 * Decided by Gideon (ADR impersonation): Admins only, Staff only, never yourself, one
 * hour at most, and every impersonation — and every change made during it — is
 * recorded.
 */
import { can } from '../access/index.ts'
import type { Actor, Verdict, WorkspaceUser } from '../users/index.ts'

/** An impersonation ends by itself after this long. */
export const IMPERSONATION_MAX_SECONDS = 60 * 60

const allow: Verdict = { ok: true }
const deny = (reason: string): Verdict => ({ ok: false, reason })

export function checkImpersonate(
  actor: Actor & { impersonating?: boolean },
  target: WorkspaceUser,
): Verdict {
  if (actor.impersonating) return deny('Stop impersonating first.')
  if (!can(actor.role, 'users.impersonate')) return deny('Only admins can impersonate people.')
  if (target.userId === null || target.status !== 'active')
    return deny('Only someone who has joined can be impersonated.')
  if (target.userId === actor.userId) return deny("You can't impersonate yourself.")
  if (target.role !== 'staff') return deny('Only Staff can be impersonated.')
  return allow
}

/** What the red bar says, and the record shows. */
export function impersonationNotice(targetName: string): string {
  return `You're currently impersonating ${targetName}. Anything you do will be as if done by them.`
}
